import { Link } from "react-router-dom";
import { Mail, Building2, Shield } from "lucide-react";
import { ORGANIZER } from "@/data/regulations";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const SECTIONS = [
  {
    n: 1,
    title: "Administrator danych",
    items: [
      "Administratorem Twoich danych osobowych jest Fundacja Priorytety, ul. Cecylii Śniegockiej 2/34, 00-430 Warszawa, NIP: 7011023132, KRS: 0000886132.",
      "W sprawach dotyczących przetwarzania danych osobowych możesz skontaktować się z nami pod adresem e-mail: biuro@fundacjapriorytety.pl.",
    ],
  },
  {
    n: 2,
    title: "Jakie dane zbieramy",
    items: [
      "Dane podawane w formularzu zapisu na kongres: imię i nazwisko, adres e-mail, numer telefonu.",
      "Dane niezbędne do wystawienia faktury (jeśli Kupujący prosi o fakturę).",
      "Dane techniczne (np. adres IP, informacje o przeglądarce) zbierane automatycznie w celach bezpieczeństwa oraz statystycznych.",
    ],
  },
  {
    n: 3,
    title: "Cele i podstawy prawne przetwarzania",
    items: [
      "Realizacja umowy zakupu biletu i organizacji Kongresu — art. 6 ust. 1 lit. b RODO.",
      "Realizacja obowiązków prawnych, w szczególności podatkowych i rachunkowych — art. 6 ust. 1 lit. c RODO.",
      "Prawnie uzasadniony interes administratora (obsługa korespondencji, dochodzenie i obrona przed roszczeniami) — art. 6 ust. 1 lit. f RODO.",
      "Marketing bezpośredni produktów i usług Fundacji — na podstawie dobrowolnej zgody, art. 6 ust. 1 lit. a RODO.",
    ],
  },
  {
    n: 4,
    title: "Okres przechowywania danych",
    items: [
      "Dane związane z umową zakupu biletu przechowujemy przez okres niezbędny do jej realizacji oraz przez czas przedawnienia roszczeń.",
      "Dane rachunkowe przechowujemy przez okres wynikający z przepisów prawa (co do zasady 5 lat).",
      "Dane przetwarzane na podstawie zgody — do czasu jej wycofania.",
    ],
  },
  {
    n: 5,
    title: "Odbiorcy danych",
    items: [
      "Dostawcy usług IT i hostingu, operatorzy płatności, podmioty wspierające organizację Kongresu — wyłącznie w zakresie niezbędnym do realizacji usług.",
      "Nie przekazujemy Twoich danych do państw trzecich poza EOG, chyba że jest to konieczne, a zapewniamy wymagane prawem zabezpieczenia.",
    ],
  },
  {
    n: 6,
    title: "Twoje prawa",
    items: [
      "Prawo dostępu do danych, ich sprostowania, usunięcia (w przypadkach przewidzianych prawem), ograniczenia przetwarzania.",
      "Prawo do przenoszenia danych i sprzeciwu wobec przetwarzania.",
      "Prawo do wycofania zgody w dowolnym momencie — bez wpływu na zgodność z prawem przetwarzania dokonanego przed wycofaniem.",
      "Prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (uodo.gov.pl).",
    ],
  },
  {
    n: 7,
    title: "Pliki cookies i narzędzia analityczne",
    items: [
      "Serwis może korzystać z plików cookies w celu zapewnienia prawidłowego działania strony oraz celów statystycznych.",
      "Ustawienia cookies możesz zmienić w swojej przeglądarce — ograniczenie plików cookies może wpłynąć na niektóre funkcje strony.",
    ],
  },
  {
    n: 8,
    title: "Zmiany polityki prywatności",
    items: [
      "Zastrzegamy prawo do zmiany niniejszej Polityki Prywatności. Aktualna wersja zawsze dostępna jest na stronie internetowej Kongresu.",
    ],
  },
];

export default function Polityka() {
  useDocumentTitle("Polityka prywatności", "Polityka prywatności mamy!kongres — jak przetwarzamy Twoje dane osobowe.");
  return (
    <div>
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-8 pb-14">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-6">Polityka prywatności</div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
          Jak dbamy o Twoje{" "}
          <span className="italic text-[#E6007A]">dane</span>.
        </h1>
        <p className="text-lg text-[#5C534D] max-w-3xl">
          Twoja prywatność jest dla nas ważna. Poniżej znajdziesz pełną informację o tym, kto, dlaczego i jak
          przetwarza Twoje dane osobowe w związku z udziałem w mamy!kongres.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-3xl bg-[#F4EFE6] p-8 md:p-10 grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C534D] mb-3">
              <Building2 className="w-4 h-4 text-[#E6007A]" /> Administrator
            </div>
            <div className="font-display text-2xl mb-2">{ORGANIZER.name}</div>
            <div className="text-[#5C534D]">
              {ORGANIZER.addressLines.map((l, i) => <div key={i}>{l}</div>)}
              <div className="mt-2">NIP: {ORGANIZER.nip}</div>
              <div>KRS: {ORGANIZER.krs}</div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C534D] mb-3">
              <Mail className="w-4 h-4 text-[#E6007A]" /> Kontakt w sprawach danych
            </div>
            <a href={`mailto:${ORGANIZER.email}`} className="font-display text-2xl hover:text-[#E6007A] transition-colors">
              {ORGANIZER.email}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <article key={s.n} className="scroll-mt-32" data-testid={`priv-sec-${s.n}`}>
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-display text-4xl md:text-5xl text-[#E6007A] leading-none">
                  § {s.n}
                </span>
                <h2 className="font-display text-2xl md:text-3xl leading-tight text-[#2B2623]">
                  {s.title}
                </h2>
              </div>
              <ol className="space-y-3 text-[#3d3733] leading-relaxed list-none">
                {s.items.map((it, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-display text-[#E6007A] shrink-0 w-8 text-right">{i + 1}.</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-[#F4EFE6] p-8 text-center">
          <Shield className="w-8 h-8 text-[#E6007A] mx-auto mb-3" />
          <p className="text-[#5C534D]">
            Zobacz również{" "}
            <Link to="/regulamin" className="text-[#E6007A] font-semibold link-underline">
              Regulamin
            </Link>{" "}
            kongresu.
          </p>
        </div>
      </section>
    </div>
  );
}
